// packages/contracts/src/identity-provider.ts

/**
 * Which identity provider a running gateway actually installed.
 *
 * ⚠ This is a CAPABILITY, not a deployment name, and the distinction is the
 * whole reason the type exists.
 *
 * The officer-web app shows a "SIT / DEMO" badge and a synthetic-data warning.
 * Both were driven off `environment !== 'production'` — a condition that is
 * unreachable by construction, because `assertDemoIdentityAllowed` is the first
 * line of `buildServer` and admits only `development`, `sit` and `test`. A
 * gateway that is answering the request at all has already proven it is not
 * production, so the check could only ever be true and the badge could only ever
 * be shown. It read as a control and was a tautology.
 *
 * What the badge actually claims is *"a passwordless identity stub is serving
 * synthetic seed data"*. That is a fact about what the server installed, so the
 * server reports it:
 *
 * - `demo-stub` — the passwordless demo provider in
 *   `services/gateway/src/identity.ts`. Any identity on the roster can be assumed
 *   with no credential, and every row behind it is synthetic seed data. A client
 *   seeing this MUST say so, loudly and permanently: this is the state in which
 *   nothing on screen is real and anyone can be anyone.
 * - `federated` — a real identity provider: WOG ADFS / GCC AAD, with SingPass or
 *   CorpPass 2FA where high identity assurance is needed (P2C3 §10.2, §10.7).
 *   Not built yet. It is declared here anyway so the client's handling of it is
 *   written, reviewed and typed BEFORE the day it starts arriving — the
 *   alternative is a client that first meets an unknown value in production and
 *   has to guess.
 *
 * ⚠ An unrecognised value is dangerous and must be treated as such by any
 * client: "I do not know what is authenticating these people" is not a reason to
 * hide a warning. Narrow at the boundary, never cast.
 *
 * ⚠ **AN ABSENT VALUE IS THE SAME DANGER, and it is the one that got through.**
 * A field the server did not send is `undefined` at runtime however confidently
 * a client's `interface` declares it a `string`, because a JSON body reaches
 * that interface by a cast. A gateway older than the bundle in front of it, a
 * proxy that drops the field, or a body carrying `""` all produce exactly the
 * state this type exists to make impossible: a passwordless demo stub serving
 * synthetic data with no warning on screen at all. So the sentence above is
 * written for three cases, not one — unrecognised, absent, and empty — and
 * {@link readIdentityProvider} exists so a client cannot express only the first.
 *
 * ⚠ What must NOT be folded in with them is the window before any response has
 * arrived. "Nothing has answered yet" is genuinely no evidence either way, and a
 * client that warned during it would be asserting a fact about a gateway it has
 * not spoken to. That state is the ABSENCE of a report, and it is why
 * {@link readIdentityProvider} takes a response's value and always returns a
 * report: a report can only exist once a response has arrived.
 *
 * ⚠ This does NOT replace the `environment` field, which still reports the
 * deployment's own `APP_ENV` from the server. The two answer different questions
 * — *where is this running* and *what is authenticating its users* — and only
 * the second one is what a synthetic-data warning turns on.
 */
export type IdentityProvider = 'demo-stub' | 'federated'

/** Every value {@link IdentityProvider} admits, for narrowing at a boundary. */
export const IDENTITY_PROVIDERS: readonly IdentityProvider[] = ['demo-stub', 'federated']

/**
 * Narrow an unknown value — a parsed JSON body, typically — to an
 * {@link IdentityProvider}.
 *
 * Returns `false` for anything unrecognised rather than defaulting to one.
 * Defaulting here would pick between "warn the user that nothing is real" and
 * "say nothing", and both defaults are wrong for the value they would be
 * guessing at.
 */
export function isIdentityProvider(value: unknown): value is IdentityProvider {
  return typeof value === 'string' && (IDENTITY_PROVIDERS as readonly string[]).includes(value)
}

/**
 * What ONE response actually said about `identityProvider`.
 *
 * ⚠ The existence of this object is the fact it carries. A client holds
 * `IdentityProviderReport | null`, and the `null` there means one thing only:
 * **no response has arrived yet**. Everything a response can say — a known
 * value, an unknown value, or nothing at all — is a report, so "the server has
 * not spoken" and "the server spoke and said nothing usable" can never collapse
 * into the same value by accident.
 *
 * That collapse is not hypothetical. It is exactly what a `string | null` field
 * did: `null` was set for both, `unknown` was derived from both, and a demo stub
 * behind a gateway that did not report the field rendered no badge and no
 * warning — the failure this whole type exists to prevent, reached through the
 * safe-looking half of the union.
 */
export interface IdentityProviderReport {
  /**
   * The string the response carried, or `null` when it carried no usable value:
   * the field absent, JSON `null`, a non-string, or `''`.
   *
   * ⚠ `null` here is NOT "unknown, so probably fine". It is "the gateway
   * answered and did not say", which per this file's contract is the dangerous
   * case and must be warned about as loudly as an unrecognised value.
   */
  readonly reported: string | null
}

/**
 * Read a response's `identityProvider` into an {@link IdentityProviderReport}.
 *
 * Takes `unknown` on purpose: the value arrives from `JSON.parse` and the only
 * honest input type is the one that forces a check. Every caller that has a
 * response has a report; no caller can obtain one without having a response.
 *
 * ⚠ **No trimming, no case folding, no normalisation** — the same discipline the
 * gateway applies to its own allow-lists. `'  '` is not `''`: it is a value the
 * server sent, it matches no known provider, and it must surface as an
 * unrecognised value quoted back verbatim rather than be tidied into silence.
 * Only a genuinely empty string is treated as "said nothing".
 */
export function readIdentityProvider(value: unknown): IdentityProviderReport {
  return { reported: typeof value === 'string' && value.length > 0 ? value : null }
}
